import { useEffect } from 'react';
import { postCustomerInfoToPreApproval } from 'externals/_services/offers.service';
import { wrapService } from 'actions/service_wrapper.actions';
import { useDispatch, useSelector } from 'react-redux';
import { useComplexState } from 'hook/helper.hook';
import { useSsnForm } from 'hook/ssnForm.hook';
import { formatPhoneNumber } from 'helpers/signup.helpers';
import { CLICK } from 'externals/_tracking/types/eventTypes.constants';
import {
  validateTotalAnnualIncome,
  validateMothersMaidenName,
  validateMonthlyRent,
  validateTotalBalanceOfAccounts,
  validateEmploymentStatus,
  validateResidence,
  validateBankAccountType,
  validateIfOffered,
  validateIsUsCitizen,
  validateCountry,
} from 'helpers/oneClickApp.helpers';
import { getMrphPageFormFactor } from 'helpers/metadataLogic.helpers';
import { getFullAttributes } from 'helpers/tracking.helpers';
import { convertProps } from 'partial/OfferManager/holder/genericOfferLogic';
import { unformatIncome, validateIncomeSchema } from 'helpers/money.helper';
import { validateSignupSSN } from 'helpers/signup.helpers.js';
import { useParams } from 'react-router-dom';
import { selectOneClickApplicationOffer } from 'actions/offers.actions';
import { fetchCreditCardById } from 'externals/_services/product.service';
import { useLogger } from 'hook/error.hook';
import { useLocation } from 'react-router-dom';
import { cleanPopupPaths } from 'helpers/path.helper';
import { useFlag } from 'hook/flag.hook';
import { goToUrlWithNewTab } from 'helpers/domHelper';
import { getPartnersCutomTrackingParams } from 'helpers/partnerCustomParamsHelper';
import { HOSTED_APP_SUBMISSION } from 'externals/_tracking/types/eventTypes.constants';
import usePhoneNumberInput from 'pages/Profile/pages/ProfileOverview/hooks/usePhoneNumberInput';
import { validateStreetwPOB } from 'helpers/signup.helpers.js';

const a_fetchCreditCardById = wrapService(fetchCreditCardById, {
  name: 'fetchCreditCardById',
});

const a_postCustomerInfoToPreApproval = wrapService(
  postCustomerInfoToPreApproval,
  { name: 'ALLY_CARD_PREQUALIFICATION' },
);

export const useOneCickApplication = ({
  modulePosition,
  eventHandler,
  pageName,
  userInfo,
  history,
  partnersCustomTrackingMeta,
}) => {
  const logger = useLogger({
    name: 'oneCickApplication.hook',
  });
  const dispatch = useDispatch();
  const { offerId } = useParams();

  const hostedAppFlag = useFlag('cc_offer_holder_hosted_app', {
    defaultConfig: null,
    forceDefaultConfigForDisabledOptimizely: true,
  });

  const oneClickApplicationconfig =
    hostedAppFlag?.config?.one_click_application?.oneClickApplication;

  const { ocfLoggedIn, loggedIn, oneClickApplicationOffer } = useSelector(
    state => ({
      ocfLoggedIn: state.app.ocfLoggedIn,
      loggedIn: state.app.loggedIn,
      oneClickApplicationOffer:
        state.oneClickApplication?.oneClickApplicationOffer,
    }),
  );

  useEffect(() => {
    if (!oneClickApplicationOffer) {
      onChangeCreditCardDetails();
    } else if (
      !hostedAppFlag?.config?.hosted_app_cards?.cards?.includes(
        oneClickApplicationOffer?.productKey,
      )
    ) {
      history.push('/dashboard');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oneClickApplicationOffer]);

  const personalInfo = {
    ...userInfo,
    ...{
      contactPhoneNumber: formatPhoneNumber(userInfo?.contactPhoneNumber),
    },
  };
  const [state, setState] = useComplexState({
    showEditModal: false,
    ssnError: false,
    personalInfo,
    workPhone: '',
    totalAnnualIncome: '',
    monthlyRent: '',
    totalBalanceOfAccounts: '',
    employmentStatus: '',
    residence: '',
    bankAccountType: '',
    ifOffered: '',
    isUsCitizen: '',
    country: '',
    motherMaidenName: '',
    isCheckedTerms: false,
    workPhoneError: false,
    totalBalanceOfAccountsError: false,
    employmentStatusError: false,
    residenceError: false,
    bankAccountTypeError: false,
    ifOfferedError: false,
    isUsCitizenError: false,
    countryError: false,
    motherMaidenNameError: false,
    totalAnnualIncomeError: false,
    monthlyRentError: false,
    isCheckedTermsError: false,
    showApprovalDecisionView: false,
    approvalDecision: null,
    isLoadingForm: false,
  });

  const clearSsnError = () => {
    setState({ ssnError: false });
  };

  const ssnForm = useSsnForm(false, clearSsnError, true);

  const phones = [
    personalInfo?.phone,
    personalInfo?.homePhone,
    personalInfo?.workPhone,
  ].filter(p => !!p);

  const firstPhoneNumberValue =
    phones[0]?.contactPhoneNumber?.value ?? personalInfo?.contactPhoneNumber;

  const phoneNumberInput = usePhoneNumberInput(
    firstPhoneNumberValue ?? '',
    true,
    phones[0]?.type?.type,
  );

  const phoneNumberInput2 = usePhoneNumberInput(
    phones[1]?.contactPhoneNumber?.value ?? '',
    true,
    phones[1]?.type?.type ?? 'home',
  );
  const phoneNumberStateList = [phoneNumberInput, phoneNumberInput2];

  const [quantityUserPhones, setQuantityUserPhones] = useComplexState({
    value: phones.length || 1,
  });

  const isEmptyPhone = phoneNumberInput => {
    if (phoneNumberInput?.contactPhoneNumber?.value === '') {
      phoneNumberInput?.update('');
      return true;
    } else {
      return false;
    }
  };

  const location = useLocation();

  const onMouseClickDetails = e => {
    eventHandler(CLICK, {
      'Click Type': 'Card Details',
    });
    const currentPath = location?.pathname;
    const cleanPath = cleanPopupPaths(currentPath);
    const finalUrl = `${cleanPath}/offerDetails/creditCard/${oneClickApplicationOffer?.creditCardId}`;
    history?.replace(finalUrl);
  };

  const onChangeCreditCardDetails = () => {
    dispatch(selectOneClickApplicationOffer(null));
    dispatch(a_fetchCreditCardById(offerId))
      .then(response => {
        dispatch(selectOneClickApplicationOffer(response));
      })
      .catch(err => {
        logger.reportAPIError(err);
        history.push('/dashboard');
      });
  };

  const setPersonalInfo = (e, personalInfo) => {
    e.preventDefault();
    if (!personalInfo) return;
    setState({ personalInfo: personalInfo });
  };

  const onChangeEditModal = modalState => {
    if (modalState) {
      eventHandler(CLICK, {
        'Click Type': 'Edit',
      });
    }
    setState({ showEditModal: modalState });
  };

  const onClickGoToDashboard = () => {
    eventHandler(CLICK, {
      'Click Type': 'Return To Dashboard',
    });
    history.push('/dashboard');
  };

  const onClickPricingAndTerms = () => {
    goToUrlWithNewTab(oneClickApplicationOffer?.tcUrl);
  };

  const onClickPrivacyPoliciy = () => {
    goToUrlWithNewTab(oneClickApplicationconfig?.privacyPoliciy);
  };

  const onClickBankDisclaimers = () => {
    goToUrlWithNewTab(oneClickApplicationconfig?.bankDisclaimers);
  };

  const onClickBankSecurity = () => {
    goToUrlWithNewTab(oneClickApplicationconfig?.bankSecurity);
  };

  const partnersCustomParams = getPartnersCutomTrackingParams(
    oneClickApplicationOffer,
    {
      pageName,
      ocfLoggedIn,
      loggedIn,
      partnersCustomTrackingMeta,
    },
  );

  let attributes = Object.assign({
    'Click Type': 'Apply',
    'Page Name': pageName,
    'Page Form Factor': getMrphPageFormFactor(),
    'Hosted Application': true,
  });

  const fullOfferMetadataAttributes = getFullAttributes({
    partner: oneClickApplicationconfig?.partner,
    modulePosition: modulePosition,
    offerMetadata: oneClickApplicationOffer,
    attributes: attributes,
    includeBackendRefCode: true,
    partnersCustomParams: partnersCustomParams,
  });

  const onChangeTotalAnnualIncome = value => {
    if (/^\d*$/.test(unformatIncome(value))) {
      var totalAnnualIncome = validateIncomeSchema(value)?.label;
      setState({
        totalAnnualIncome: totalAnnualIncome,
        totalAnnualIncomeError: validateTotalAnnualIncome(
          unformatIncome(totalAnnualIncome),
        ),
      });
    } else {
      let totalAnnualIncome = '';
      setState({
        totalAnnualIncome: totalAnnualIncome,
        totalAnnualIncomeError: validateTotalAnnualIncome(totalAnnualIncome),
      });
    }
  };
  const onChangeMonthlyRent = value => {
    if (/^\d*$/.test(unformatIncome(value))) {
      var monthlyRent = validateIncomeSchema(value)?.label;
      setState({
        monthlyRent: monthlyRent,
        monthlyRentError: validateMonthlyRent(unformatIncome(monthlyRent)),
      });
    } else {
      let monthlyRent = '';
      setState({
        monthlyRent: monthlyRent,
        monthlyRentError: validateMonthlyRent(monthlyRent),
      });
    }
  };
  const onChangeMotherMaidenName = motherMaidenName => {
    setState({
      motherMaidenName: motherMaidenName,
      motherMaidenNameError: validateMothersMaidenName(motherMaidenName),
    });
  };

  const postCustomerInfoToPreApprovalOneClick = () => {
    const propsForBackend = convertProps(fullOfferMetadataAttributes);
    const totalAnnualIncomeError = validateTotalAnnualIncome(
      unformatIncome(state.totalAnnualIncome),
    );
    const monthlyRentError = validateTotalAnnualIncome(
      unformatIncome(state.totalAnnualIncome),
    );
    const motherMaidenNameError = validateMothersMaidenName(
      state.motherMaidenName,
    );
    const totalBalanceOfAccountsError = validateTotalBalanceOfAccounts(
      state.totalBalanceOfAccounts,
    );
    const employmentStatusError = validateEmploymentStatus(
      state.employmentStatus,
    );
    const residenceError = validateResidence(state.residence);
    const bankAccountTypeError = validateBankAccountType(state.bankAccountType);
    const ifOfferedError = validateIfOffered(state.ifOffered);
    const isUsCitizenError = validateIsUsCitizen(state.isUsCitizen);
    const countryError = state.isUsCitizen
      ? false
      : validateCountry(state.country);
    const ssnError = validateSignupSSN(ssnForm?.ssn?.value);
    const phoneError = isEmptyPhone(phoneNumberInput);
    const phoneError2 =
      quantityUserPhones?.value > 1 ? isEmptyPhone(phoneNumberInput2) : false;
    const streetError = validateStreetwPOB(
      state?.personalInfo?.primaryAddress?.street,
    );

    setState({
      totalAnnualIncomeError,
      monthlyRentError,
      motherMaidenNameError,
      ssnError,
      totalBalanceOfAccountsError,
      employmentStatusError,
      residenceError,
      bankAccountTypeError,
      ifOfferedError,
      isUsCitizenError,
      countryError,
    });

    let newPhoneList =
      quantityUserPhones?.value > 1
        ? phoneNumberStateList
        : [phoneNumberStateList[0]];

    const phoneValue =
      newPhoneList?.find(phone => phone?.type?.type === 'mobile') ?? null;
    const homePhoneValue =
      newPhoneList?.find(phone => phone?.type?.type === 'home') ?? null;
    const workPhoneValue =
      newPhoneList?.find(phone => phone?.type?.type === 'other') ?? null;

    const validationErrorFields =
      totalAnnualIncomeError ||
      monthlyRentError ||
      motherMaidenNameError ||
      totalBalanceOfAccountsError ||
      employmentStatusError ||
      residenceError ||
      bankAccountTypeError ||
      ifOfferedError ||
      isUsCitizenError ||
      countryError ||
      ssnError ||
      phoneNumberInput?.contactPhoneNumber?.error ||
      phoneNumberInput2?.contactPhoneNumber?.error ||
      phoneError ||
      phoneError2 ||
      streetError;

    if (!validationErrorFields) {
      setState({ isLoadingForm: true });
      const customerInfo = {
        partner: oneClickApplicationconfig?.partner,
        issuer: oneClickApplicationconfig?.issuer,
        consumer: {
          contact: {
            first: state?.personalInfo?.firstName,
            last: state?.personalInfo?.lastName,
            email: state?.personalInfo?.email,
            middle: state?.personalInfo?.middleName,
            dob: state?.personalInfo?.birthDate,
            ssn: ssnForm?.ssn?.value,
            phone:
              phoneValue?.contactPhoneNumber?.displayValue?.replace(
                /\s/g,
                '',
              ) ?? state?.personalInfo?.contactPhoneNumber?.replace(/\s/g, ''),
            homePhone:
              homePhoneValue?.contactPhoneNumber?.displayValue?.replace(
                /\s/g,
                '',
              ),
            workPhone:
              workPhoneValue?.contactPhoneNumber?.displayValue?.replace(
                /\s/g,
                '',
              ),
          },
          address: {
            city: state?.personalInfo?.primaryAddress?.city,
            state: state?.personalInfo?.primaryAddress?.state,
            street: state?.personalInfo?.primaryAddress?.street,
            zip: state?.personalInfo?.primaryAddress?.zip,
          },
        },
        params: {
          investment: 1,
          motherMaidenName: state?.motherMaidenName,
          residenceType: state?.residence,
          housingPaymentMonthly: Number(unformatIncome(state?.monthlyRent)),
          bankingAccounts: state?.bankAccountType,
          bankingBalance: state?.totalBalanceOfAccounts,
          citizenshipCountry: state?.isUsCitizen ? 'USA' : state?.country,
          utmCampaign: oneClickApplicationconfig?.utmCampaign,
          misc: oneClickApplicationconfig?.misc,
        },
        userId: userInfo?.userId,
        productId: oneClickApplicationOffer?.productId,
        productKey: oneClickApplicationOffer?.productKey,
        income: Number(unformatIncome(state?.totalAnnualIncome)),
        incomeType: state?.employmentStatus,
        trackingInfo: propsForBackend,
      };
      dispatch(a_postCustomerInfoToPreApproval(customerInfo))
        .then(response => {
          let approvalDecision = response;
          eventHandler(
            HOSTED_APP_SUBMISSION,
            Object.assign(attributes, fullOfferMetadataAttributes, {
              'Click ID': approvalDecision?.clickId,
              'Hosted App Decision': approvalDecision?.preApprovalDecision,
            }),
          );
          setState({
            showApprovalDecisionView: true,
            approvalDecision: approvalDecision,
            isLoadingForm: false,
          });
        })
        .catch(err => {
          logger.reportAPIError(err);
          let errorCode = err?.errorCode;
          if (errorCode === 'SSNV_1003') {
            setState({
              ssnError:
                'The last 4 digits of the social security number do not match',
              isLoadingForm: false,
            });
          } else {
            setState({
              showApprovalDecisionView: true,
              approvalDecision: {
                genericData: {
                  status: 'ERROR',
                },
              },
              isLoadingForm: false,
            });
          }
        });
    }
  };

  return {
    oneClickApplicationconfig,
    postCustomerInfoToPreApprovalOneClick,
    onChangeEditModal,
    ssnForm,
    setState,
    phoneNumberStateList,
    setPersonalInfo,
    oneClickApplicationOffer,
    onMouseClickDetails,
    onClickGoToDashboard,
    onClickPricingAndTerms,
    onClickPrivacyPoliciy,
    onClickBankDisclaimers,
    onChangeTotalAnnualIncome,
    onChangeMonthlyRent,
    onChangeMotherMaidenName,
    onClickBankSecurity,
    quantityUserPhones,
    setQuantityUserPhones,
    ...state,
  };
};
